import React from 'react';
import { Box, Grid, Text, VStack, HStack, Icon } from '@chakra-ui/react';
import { ShieldCheck, Users, Crosshair, TrendingUp } from 'lucide-react';
import { SectionContainer } from '../SectionContainer';
import { ScrollReveal } from '../ScrollReveal';

const points = [
  {
    icon: ShieldCheck,
    label: 'Trust is earned.',
    description: 'Every source carries an evolving trust score based on its track record.',
  },
  {
    icon: Users,
    label: 'Knowledge is peer-reviewed.',
    description: 'New learnings are validated against the consensus of the system.',
  },
  {
    icon: Crosshair,
    label: 'Facts beat opinions.',
    description:
      'Wrong facts are penalized aggressively; the system polices its own accuracy.',
  },
  {
    icon: TrendingUp,
    label: 'Useful rises, stale fades.',
    description:
      'Valuable knowledge gains weight; unused knowledge naturally decays.',
  },
];

function LearningPoint({ point, index }) {
  return (
    <ScrollReveal delay={index * 0.1}>
      <HStack
        align="flex-start"
        spacing={4}
        p={{ base: '20px', md: '24px' }}
        bg="bg.700"
        border="1px solid"
        borderColor="whiteAlpha.100"
        borderRadius="16px"
        _hover={{ borderColor: 'brand.500', boxShadow: '0 4px 20px rgba(99,102,241,0.1)' }}
        sx={{ transition: 'all 0.25s ease' }}
        role="listitem"
      >
        <Box
          w="40px"
          h="40px"
          flexShrink={0}
          borderRadius="10px"
          bg="rgba(56,189,248,0.1)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          aria-hidden="true"
        >
          <Icon as={point.icon} color="brand.400" boxSize={5} />
        </Box>
        <VStack align="flex-start" spacing={1}>
          <Text
            fontFamily="'Space Grotesk', sans-serif"
            fontWeight="700"
            fontSize="1rem"
            color="text.primary"
          >
            {point.label}
          </Text>
          <Text fontSize="0.95rem" color="text.muted" lineHeight="1.6">
            {point.description}
          </Text>
        </VStack>
      </HStack>
    </ScrollReveal>
  );
}

export default function LearningSection() {
  return (
    <SectionContainer bg="bg.900" id="learning" aria-labelledby="learning-heading">
      <VStack spacing={{ base: 10, md: 14 }} align="stretch">
        <ScrollReveal>
          <Text
            id="learning-heading"
            as="h2"
            fontFamily="'Space Grotesk', sans-serif"
            fontWeight="700"
            fontSize={{ base: 'clamp(1.8rem, 5vw, 2rem)', md: 'clamp(2rem, 3.5vw, 2.75rem)' }}
            letterSpacing="-0.02em"
            color="text.primary"
            maxW="720px"
          >
            It doesn't just act. It learns —{' '}
            <Box as="span" color="brand.400">
              and earns trust.
            </Box>
          </Text>
        </ScrollReveal>

        <Grid
          templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
          gap={{ base: 4, md: 5 }}
          role="list"
          aria-label="Learning system principles"
        >
          {points.map((point, i) => (
            <LearningPoint key={point.label} point={point} index={i} />
          ))}
        </Grid>

        {/* Pull quote */}
        <ScrollReveal delay={0.2}>
          <Box
            borderTop="1px solid"
            borderColor="whiteAlpha.100"
            pt={{ base: 8, md: 12 }}
            textAlign="center"
          >
            <Text
              fontFamily="'Space Grotesk', sans-serif"
              fontWeight="500"
              fontSize={{ base: '1.2rem', md: '1.6rem' }}
              lineHeight="1.4"
              color="brand.400"
              maxW="760px"
              mx="auto"
              as="blockquote"
              fontStyle="italic"
            >
              "A memory that continuously asks itself how much it deserves to be believed."
            </Text>
          </Box>
        </ScrollReveal>
      </VStack>
    </SectionContainer>
  );
}